import { Direction } from './engine/enums';
import Vector2d from './engine/vector2d';
import Maze from './maze';
import State from './state';

export const moveEntity = (entity: Entity, speed: number, state: State): void => {
  const {
    position,
    target,
    direction,
    nextDirection
  } = entity;

  let { x, y } = target;

  const maxX = Maze.maxX(state);
  const maxY = Maze.maxY(state);

  if (shouldUpdateTarget(entity)) {
    const newDir = Maze.canMove(getNextPosition(nextDirection, target), state) && nextDirection !== direction;

    if (newDir) {
      entity.direction = nextDirection;
    }

    switch (newDir ? nextDirection : direction) {
      case Direction.Left:
        x--;
        break;
      case Direction.Right:
        x++;
        break;
      case Direction.Up:
        y--;
        break;
      case Direction.Down:
        y++;
        break;
    }

    if (x < 0) {
      x = maxX;
    } else if (x > maxX) {
      x = 0;
    }

    if (y < 0) {
      y = maxY;
    } else if (y > maxY) {
      y = 0;
    }

    const newPos = new Vector2d(x, y);
    if (Maze.canMove(newPos, state)) {
      entity.target = newPos;
    }
  } else {
    let { x, y } = position;
    switch (direction) {
      case Direction.Left:
        x -= speed;
        x = Math.max(x, target.x);
        break;
      case Direction.Right:
        x += speed;
        x = Math.min(x, target.x);
        break;
      case Direction.Up:
        y -= speed;
        y = Math.max(y, target.y);
        break;
      case Direction.Down:
        y += speed;
        y = Math.min(y, target.y);
        break;
    }

    if (x < 0) {
      x = maxX;
    } else if (x > maxX) {
      x = 0;
    }

    if (y < 0) {
      y = maxY;
    } else if (y > maxY) {
      y = 0;
    }

    entity.position = new Vector2d(x, y);
  }
};

const shouldUpdateTarget = (entity: Entity): boolean => {
  return Vector2d.isSame(entity.position, entity.target);
};

export const getNextPosition = (nextDirection: Direction, target: Vector2d): Vector2d => {
  let { x, y } = target;

  switch (nextDirection) {
    case Direction.Right:
      x++;
      break;
    case Direction.Left:
      x--;
      break;
    case Direction.Down:
      y++;
      break;
    case Direction.Up:
      y--;
      break;
  }

  return new Vector2d(x, y);
};

export default class Entity {
  private _originalPosition: Vector2d;
  public get originalPosition(): Vector2d {
    return this._originalPosition;
  }

  public get position(): Vector2d {
    return this._position;
  }

  public set position(value: Vector2d) {
    this._position = value;
  }

  public get target(): Vector2d {
    return this._target;
  }

  public set target(value: Vector2d) {
    this._target = value;
  }

  public get direction(): Direction {
    return this._direction;
  }

  public set direction(value: Direction) {
    this._direction = value;
  }

  private _nextDirection: Direction;
  public get nextDirection(): Direction {
    return this._nextDirection;
  }

  public set nextDirection(value: Direction) {
    this._nextDirection = value;
  }

  private _isRespawning: boolean = false;
  public get isRespawning(): boolean {
    return this._isRespawning;
  }

  public set isRespawning(value: boolean) {
    this._isRespawning = value;
  }

  public get isDead(): boolean {
    return this.lives < 1;
  }

  private _lives: number = 0;
  public get lives() {
    return this._lives;
  }

  public set lives(value: number) {
    if (value >= 0) {
      this._lives = value;
    }
  }

  private _powerupTimer: number = 0;
  public get powerupTimer() {
    return this._powerupTimer;
  }

  public get hasPowerup(): boolean {
    if (this._powerupTimer > 0) {
      this._powerupTimer -= .4;
    }

    return this._powerupTimer > 0;
  }

  public set hasPowerup(value: boolean) {
    if (value) {
      this._powerupTimer = 500;
    } else {
      this._powerupTimer = 0;
    }
  }

  public get name() {
    return this._name;
  }

  private _score: number = 0;
  public get score() {
    return this._score;
  }

  public set score(score: number) {
    this._score = score;
  }

  public get displayScore() {
    const score = this.score.toString();
    return `${'0000000000'.substr(0, 10 - score.length)}${score}`;
  }

  constructor(private _position: Vector2d, private _target: Vector2d, private _direction: Direction, private _name?: string)  {
    this._nextDirection = _direction;
    this._originalPosition = _position;
  }
}
